import {
  Loader2,
  CreditCard,
  AlertCircle,
  Package,
  Wallet,
  Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useCheckout } from "@/features/cart";
import { useBalance } from "@/features/deposits";
import { Button } from "@/shared/ui/kit/button";
import { Price } from "@/shared/ui/price";

type CartSummaryProps = {
  totalCents: number;
  itemCount: number;
  hasOutOfStock: boolean;
  isPending?: boolean;
  onTopUp?: () => void;
};

export function CartSummary({
  totalCents,
  itemCount,
  hasOutOfStock,
  isPending = false,
  onTopUp,
}: CartSummaryProps) {
  const { t } = useTranslation();
  const checkout = useCheckout();
  const { balance } = useBalance();

  const hasInsufficientBalance =
    balance !== undefined && balance.amount < totalCents;

  const handleCheckout = async () => {
    const idempotencyKey = crypto.randomUUID();
    try {
      await checkout.mutateAsync({
        body: { idempotency_key: idempotencyKey },
      });
      toast.success(t("cart.success.checkout"));
    } catch (error) {
      const message =
        error && typeof error === "object" && "error" in error
          ? (error.error as { message?: string })?.message
          : t("cart.error.checkoutFailed");
      toast.error(message || t("cart.error.checkoutFailed"));
    }
  };

  const isCheckoutDisabled =
    itemCount === 0 || checkout.isPending || hasOutOfStock;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">
            {t("cart.summary.items", { defaultValue: "Items" })}
          </span>
          <span>{itemCount}</span>
        </div>
        <div className="flex justify-between text-lg font-semibold">
          <span>{t("cart.summary.total", { defaultValue: "Total" })}</span>
          <span>
            <Price cents={totalCents} />
          </span>
        </div>
      </div>

      {hasOutOfStock && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
          <Package className="size-4 shrink-0" />
          <span>
            {t("cart.summary.outOfStock", {
              defaultValue: "Some items are out of stock",
            })}
          </span>
        </div>
      )}

      {hasInsufficientBalance && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
          <AlertCircle className="size-4 shrink-0" />
          <span>
            {t("cart.summary.insufficientBalance", {
              defaultValue: "Недостаточно средств на балансе",
            })}
          </span>
        </div>
      )}

      {hasInsufficientBalance ? (
        <Button className="w-full" size="lg" onClick={onTopUp}>
          <Wallet className="size-4" />
          {t("cart.topup", { defaultValue: "Пополнить баланс" })}
        </Button>
      ) : (
        <Button
          className="w-full"
          size="lg"
          onClick={handleCheckout}
          disabled={isCheckoutDisabled}
        >
          {checkout.isPending || isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t("cart.checkout", { defaultValue: "Checkout" })}
            </>
          ) : checkout.isSuccess ? (
            <>
              <Check className="size-4" />
              {t("cart.success", { defaultValue: "Success!" })}
            </>
          ) : (
            <>
              <CreditCard className="size-4" />
              {t("cart.checkout", { defaultValue: "Checkout" })}
            </>
          )}
        </Button>
      )}
    </div>
  );
}
